export var Role;
(function (Role) {
    // Organization-level roles
    Role["ORG_OWNER"] = "ORG_OWNER";
    Role["ORG_ADMINISTRATOR"] = "ORG_ADMINISTRATOR";
    Role["ORG_MANAGER"] = "ORG_MANAGER";
    Role["ORG_MEDICAL_PROFESSIONAL"] = "ORG_MEDICAL_PROFESSIONAL";
    Role["ORG_ACCOUNTANT"] = "ORG_ACCOUNTANT";
    Role["ORG_VIEWER"] = "ORG_VIEWER";
})(Role || (Role = {}));
// Admin RBAC lives in `./admin/AdminAuthorization.ts` — `AdminRole`,
// `AdminAction`, `AdminGrant`, and `hasAdminPermission`. Tenant authz and admin
// authz are deliberately disjoint: tenant authorization resolves through
// `authorizationService.getAccessProfile(userId, organizationId)` which is
// org-scoped, while admin actions are platform-wide and have no
// `organizationId` to scope by.
// ============================================================================
// UNIFIED CAPABILITY-BASED RBAC
// ============================================================================
export var Action;
(function (Action) {
    // Organization governance
    Action["VIEW_SETTINGS"] = "VIEW_SETTINGS";
    Action["MANAGE_SETTINGS"] = "MANAGE_SETTINGS";
    Action["DELETE_ORGANIZATION"] = "DELETE_ORGANIZATION";
    // Staff & Establishments
    Action["VIEW_STAFF"] = "VIEW_STAFF";
    Action["MANAGE_STAFF"] = "MANAGE_STAFF";
    Action["INVITE_STAFF"] = "INVITE_STAFF";
    // PlatformPlan
    Action["VIEW_PLATFORM_PLAN"] = "VIEW_PLATFORM_PLAN";
    Action["MANAGE_PLATFORM_PLAN"] = "MANAGE_PLATFORM_PLAN";
    // AI provider credentials (BYOK) — least-privilege, distinct from MANAGE_PLATFORM_PLAN
    // so managing provider secrets is not bundled with billing/plan management.
    Action["MANAGE_AI_CREDENTIALS"] = "MANAGE_AI_CREDENTIALS";
    Action["VIEW_ESTABLISHMENTS"] = "VIEW_ESTABLISHMENTS";
    Action["MANAGE_ESTABLISHMENTS"] = "MANAGE_ESTABLISHMENTS";
    // Analytics
    Action["VIEW_ANALYTICS"] = "VIEW_ANALYTICS";
    // ============================================
    // GRASS PRODUCTION MODULE ACTIONS
    // ============================================
    // Farm Management
    Action["VIEW_FARMS"] = "VIEW_FARMS";
    Action["MANAGE_FARMS"] = "MANAGE_FARMS";
    // Paddock Management
    Action["VIEW_PADDOCKS"] = "VIEW_PADDOCKS";
    Action["MANAGE_PADDOCKS"] = "MANAGE_PADDOCKS";
    // Cultivar Management
    Action["VIEW_CULTIVARS"] = "VIEW_CULTIVARS";
    Action["MANAGE_CULTIVARS"] = "MANAGE_CULTIVARS";
    // Field Operations & Measurements
    Action["VIEW_OPERATIONS"] = "VIEW_OPERATIONS";
    Action["CREATE_OPERATIONS"] = "CREATE_OPERATIONS";
    Action["MANAGE_OPERATIONS"] = "MANAGE_OPERATIONS";
    Action["VIEW_MEASUREMENTS"] = "VIEW_MEASUREMENTS";
    Action["CREATE_MEASUREMENTS"] = "CREATE_MEASUREMENTS";
    Action["MANAGE_MEASUREMENTS"] = "MANAGE_MEASUREMENTS";
    // Planning & Work Orders
    Action["VIEW_PLANS"] = "VIEW_PLANS";
    Action["MANAGE_PLANS"] = "MANAGE_PLANS";
    Action["APPROVE_WORK_ORDERS"] = "APPROVE_WORK_ORDERS";
    // Analytics & Forecasts
    Action["VIEW_FORECASTS"] = "VIEW_FORECASTS";
    Action["VIEW_RECOMMENDATIONS"] = "VIEW_RECOMMENDATIONS";
    Action["APPROVE_RECOMMENDATIONS"] = "APPROVE_RECOMMENDATIONS";
    // ============================================
    // EQUIPMENT MODULE ACTIONS
    // ============================================
    Action["VIEW_EQUIPMENT"] = "VIEW_EQUIPMENT";
    Action["MANAGE_EQUIPMENT"] = "MANAGE_EQUIPMENT";
    Action["VIEW_PARTS_INVENTORY"] = "VIEW_PARTS_INVENTORY";
    Action["MANAGE_PARTS_INVENTORY"] = "MANAGE_PARTS_INVENTORY";
    Action["PERFORM_INSPECTIONS"] = "PERFORM_INSPECTIONS";
    Action["REPORT_BREAKDOWNS"] = "REPORT_BREAKDOWNS";
    // ============================================
    // LIVESTOCK MODULE ACTIONS
    // ============================================
    Action["VIEW_LIVESTOCK"] = "VIEW_LIVESTOCK";
    Action["VIEW_LIVESTOCK_RECORDS"] = "VIEW_LIVESTOCK_RECORDS";
    Action["VIEW_LIVESTOCK_MOVEMENTS"] = "VIEW_LIVESTOCK_MOVEMENTS";
    Action["MANAGE_LIVESTOCK"] = "MANAGE_LIVESTOCK";
    // ============================================
    // ECONOMICS / FEASIBILITY MODULE ACTIONS
    // ============================================
    Action["VIEW_FEASIBILITY"] = "VIEW_FEASIBILITY";
    Action["MANAGE_FEASIBILITY"] = "MANAGE_FEASIBILITY";
    // ============================================
    // FINANCIAL HUB MODULE ACTIONS
    // ============================================
    Action["VIEW_FINANCE"] = "VIEW_FINANCE";
    Action["MANAGE_FINANCE"] = "MANAGE_FINANCE";
    Action["MANAGE_FINANCE_SETTINGS"] = "MANAGE_FINANCE_SETTINGS";
    Action["EXPORT_FINANCE"] = "EXPORT_FINANCE";
    // Payroll — a dedicated, salary-sensitive action distinct from MANAGE_FINANCE.
    // Granted to ORG_OWNER / ORG_ADMINISTRATOR / ORG_ACCOUNTANT only (NOT
    // ORG_MANAGER, who holds VIEW_FINANCE): every payroll route gates on it so
    // individual salaries never reach a plain finance viewer (WP2 §4.8).
    Action["MANAGE_PAYROLL"] = "MANAGE_PAYROLL";
    // ============================================
    // AI ASSISTANT
    // ============================================
    Action["USE_AI_ASSISTANT"] = "USE_AI_ASSISTANT";
    // Review (list, edit, approve, reject) AI-generated action proposals before
    // any domain write executes. Distinct from USE_AI_ASSISTANT: chatting with
    // the assistant is not the same trust level as approving the writes it
    // proposes. Granted to every org role except ORG_VIEWER.
    Action["REVIEW_AI_PROPOSALS"] = "REVIEW_AI_PROPOSALS";
    // ============================================
    // ACTIVITY / AUDIT MODULE ACTIONS
    // ============================================
    // Read the org-wide activity-events feed (who did what). Personal-data
    // sensitive: granted to ORG_OWNER / ORG_ADMINISTRATOR only so staff cannot
    // surveil each other. See activity-events two-tier audit decision record.
    Action["VIEW_ACTIVITY"] = "VIEW_ACTIVITY";
})(Action || (Action = {}));
export var Scope;
(function (Scope) {
    Scope["ORG"] = "ORG";
})(Scope || (Scope = {}));
// ----------------------------------------------------------------------------
// DEFAULT ROLE DEFINITIONS (mapped from current OrganizationRole/EstablishmentRole)
// ----------------------------------------------------------------------------
// ----------------------------------------------------------------------------
// PERMISSION CHECK UTILITIES
// ----------------------------------------------------------------------------
/**
 * Check if a user has permission to perform an action based on their access profile.
 * This is the single source of truth for permission checking, shared between frontend and backend.
 *
 * @param profile - The user's access profile
 * @param action - The action to check permission for
 * @returns true if the user has permission, false otherwise
 */
export function hasPermission(profile, action) {
    return profile.grants.some((g) => g.action === action && g.scope === Scope.ORG);
}
// ----------------------------------------------------------------------------
// DEFAULT ROLE DEFINITIONS (mapped from current OrganizationRole/EstablishmentRole)
// ----------------------------------------------------------------------------
export const DEFAULT_ROLE_DEFINITIONS = [
    // Organization-level roles
    {
        id: Role.ORG_OWNER,
        name: "Organization Owner",
        grants: [
            { action: Action.VIEW_SETTINGS, scope: Scope.ORG },
            { action: Action.MANAGE_SETTINGS, scope: Scope.ORG },
            { action: Action.DELETE_ORGANIZATION, scope: Scope.ORG },
            { action: Action.VIEW_STAFF, scope: Scope.ORG },
            { action: Action.MANAGE_STAFF, scope: Scope.ORG },
            { action: Action.INVITE_STAFF, scope: Scope.ORG },
            { action: Action.VIEW_ESTABLISHMENTS, scope: Scope.ORG },
            { action: Action.MANAGE_ESTABLISHMENTS, scope: Scope.ORG },
            { action: Action.VIEW_PLATFORM_PLAN, scope: Scope.ORG },
            { action: Action.MANAGE_PLATFORM_PLAN, scope: Scope.ORG },
            { action: Action.MANAGE_AI_CREDENTIALS, scope: Scope.ORG },
            { action: Action.VIEW_ANALYTICS, scope: Scope.ORG },
            // GPPMS: Full access
            { action: Action.VIEW_FARMS, scope: Scope.ORG },
            { action: Action.MANAGE_FARMS, scope: Scope.ORG },
            { action: Action.VIEW_PADDOCKS, scope: Scope.ORG },
            { action: Action.MANAGE_PADDOCKS, scope: Scope.ORG },
            { action: Action.VIEW_CULTIVARS, scope: Scope.ORG },
            { action: Action.MANAGE_CULTIVARS, scope: Scope.ORG },
            { action: Action.VIEW_OPERATIONS, scope: Scope.ORG },
            { action: Action.CREATE_OPERATIONS, scope: Scope.ORG },
            { action: Action.MANAGE_OPERATIONS, scope: Scope.ORG },
            { action: Action.VIEW_MEASUREMENTS, scope: Scope.ORG },
            { action: Action.CREATE_MEASUREMENTS, scope: Scope.ORG },
            { action: Action.MANAGE_MEASUREMENTS, scope: Scope.ORG },
            { action: Action.VIEW_PLANS, scope: Scope.ORG },
            { action: Action.MANAGE_PLANS, scope: Scope.ORG },
            { action: Action.APPROVE_WORK_ORDERS, scope: Scope.ORG },
            { action: Action.VIEW_FORECASTS, scope: Scope.ORG },
            { action: Action.VIEW_RECOMMENDATIONS, scope: Scope.ORG },
            { action: Action.APPROVE_RECOMMENDATIONS, scope: Scope.ORG },
            // Equipment: Full access
            { action: Action.VIEW_EQUIPMENT, scope: Scope.ORG },
            { action: Action.MANAGE_EQUIPMENT, scope: Scope.ORG },
            { action: Action.VIEW_PARTS_INVENTORY, scope: Scope.ORG },
            { action: Action.MANAGE_PARTS_INVENTORY, scope: Scope.ORG },
            { action: Action.PERFORM_INSPECTIONS, scope: Scope.ORG },
            { action: Action.REPORT_BREAKDOWNS, scope: Scope.ORG },
            // Livestock: Full access
            { action: Action.VIEW_LIVESTOCK, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_RECORDS, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_MOVEMENTS, scope: Scope.ORG },
            { action: Action.MANAGE_LIVESTOCK, scope: Scope.ORG },
            // Economics / Feasibility: Full access
            { action: Action.VIEW_FEASIBILITY, scope: Scope.ORG },
            { action: Action.MANAGE_FEASIBILITY, scope: Scope.ORG },
            // Financial Hub: Full access
            { action: Action.VIEW_FINANCE, scope: Scope.ORG },
            { action: Action.MANAGE_FINANCE, scope: Scope.ORG },
            { action: Action.MANAGE_FINANCE_SETTINGS, scope: Scope.ORG },
            { action: Action.EXPORT_FINANCE, scope: Scope.ORG },
            // Payroll: salary-sensitive, owner/admin/accountant only (NOT manager).
            { action: Action.MANAGE_PAYROLL, scope: Scope.ORG },
            // AI Assistant
            { action: Action.USE_AI_ASSISTANT, scope: Scope.ORG },
            { action: Action.REVIEW_AI_PROPOSALS, scope: Scope.ORG },
            // Activity feed: personal-data sensitive, owner/admin only.
            { action: Action.VIEW_ACTIVITY, scope: Scope.ORG },
        ],
    },
    {
        id: Role.ORG_ADMINISTRATOR,
        name: "Organization Administrator",
        grants: [
            { action: Action.VIEW_SETTINGS, scope: Scope.ORG },
            { action: Action.MANAGE_SETTINGS, scope: Scope.ORG },
            { action: Action.VIEW_STAFF, scope: Scope.ORG },
            { action: Action.MANAGE_STAFF, scope: Scope.ORG },
            { action: Action.INVITE_STAFF, scope: Scope.ORG },
            { action: Action.VIEW_ESTABLISHMENTS, scope: Scope.ORG },
            { action: Action.MANAGE_ESTABLISHMENTS, scope: Scope.ORG },
            { action: Action.VIEW_PLATFORM_PLAN, scope: Scope.ORG },
            { action: Action.MANAGE_PLATFORM_PLAN, scope: Scope.ORG },
            { action: Action.MANAGE_AI_CREDENTIALS, scope: Scope.ORG },
            { action: Action.VIEW_ANALYTICS, scope: Scope.ORG },
            // GPPMS: Full access
            { action: Action.VIEW_FARMS, scope: Scope.ORG },
            { action: Action.MANAGE_FARMS, scope: Scope.ORG },
            { action: Action.VIEW_PADDOCKS, scope: Scope.ORG },
            { action: Action.MANAGE_PADDOCKS, scope: Scope.ORG },
            { action: Action.VIEW_CULTIVARS, scope: Scope.ORG },
            { action: Action.MANAGE_CULTIVARS, scope: Scope.ORG },
            { action: Action.VIEW_OPERATIONS, scope: Scope.ORG },
            { action: Action.CREATE_OPERATIONS, scope: Scope.ORG },
            { action: Action.MANAGE_OPERATIONS, scope: Scope.ORG },
            { action: Action.VIEW_MEASUREMENTS, scope: Scope.ORG },
            { action: Action.CREATE_MEASUREMENTS, scope: Scope.ORG },
            { action: Action.MANAGE_MEASUREMENTS, scope: Scope.ORG },
            { action: Action.VIEW_PLANS, scope: Scope.ORG },
            { action: Action.MANAGE_PLANS, scope: Scope.ORG },
            { action: Action.APPROVE_WORK_ORDERS, scope: Scope.ORG },
            { action: Action.VIEW_FORECASTS, scope: Scope.ORG },
            { action: Action.VIEW_RECOMMENDATIONS, scope: Scope.ORG },
            { action: Action.APPROVE_RECOMMENDATIONS, scope: Scope.ORG },
            // Equipment: Full access
            { action: Action.VIEW_EQUIPMENT, scope: Scope.ORG },
            { action: Action.MANAGE_EQUIPMENT, scope: Scope.ORG },
            { action: Action.VIEW_PARTS_INVENTORY, scope: Scope.ORG },
            { action: Action.MANAGE_PARTS_INVENTORY, scope: Scope.ORG },
            { action: Action.PERFORM_INSPECTIONS, scope: Scope.ORG },
            { action: Action.REPORT_BREAKDOWNS, scope: Scope.ORG },
            // Livestock: Full access
            { action: Action.VIEW_LIVESTOCK, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_RECORDS, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_MOVEMENTS, scope: Scope.ORG },
            { action: Action.MANAGE_LIVESTOCK, scope: Scope.ORG },
            // Economics / Feasibility: Full access
            { action: Action.VIEW_FEASIBILITY, scope: Scope.ORG },
            { action: Action.MANAGE_FEASIBILITY, scope: Scope.ORG },
            // Financial Hub: Full access
            { action: Action.VIEW_FINANCE, scope: Scope.ORG },
            { action: Action.MANAGE_FINANCE, scope: Scope.ORG },
            { action: Action.MANAGE_FINANCE_SETTINGS, scope: Scope.ORG },
            { action: Action.EXPORT_FINANCE, scope: Scope.ORG },
            // Payroll: salary-sensitive, owner/admin/accountant only (NOT manager).
            { action: Action.MANAGE_PAYROLL, scope: Scope.ORG },
            // AI Assistant
            { action: Action.USE_AI_ASSISTANT, scope: Scope.ORG },
            { action: Action.REVIEW_AI_PROPOSALS, scope: Scope.ORG },
            // Activity feed: personal-data sensitive, owner/admin only.
            { action: Action.VIEW_ACTIVITY, scope: Scope.ORG },
        ],
    },
    {
        id: Role.ORG_MANAGER,
        name: "Organization Manager",
        grants: [
            { action: Action.VIEW_SETTINGS, scope: Scope.ORG },
            { action: Action.VIEW_STAFF, scope: Scope.ORG },
            { action: Action.MANAGE_STAFF, scope: Scope.ORG },
            { action: Action.INVITE_STAFF, scope: Scope.ORG },
            { action: Action.VIEW_ESTABLISHMENTS, scope: Scope.ORG },
            { action: Action.MANAGE_ESTABLISHMENTS, scope: Scope.ORG },
            { action: Action.VIEW_ANALYTICS, scope: Scope.ORG },
            // GPPMS: View + Create (no full manage)
            { action: Action.VIEW_FARMS, scope: Scope.ORG },
            { action: Action.VIEW_PADDOCKS, scope: Scope.ORG },
            { action: Action.MANAGE_PADDOCKS, scope: Scope.ORG },
            { action: Action.VIEW_CULTIVARS, scope: Scope.ORG },
            { action: Action.VIEW_OPERATIONS, scope: Scope.ORG },
            { action: Action.CREATE_OPERATIONS, scope: Scope.ORG },
            { action: Action.VIEW_MEASUREMENTS, scope: Scope.ORG },
            { action: Action.CREATE_MEASUREMENTS, scope: Scope.ORG },
            { action: Action.VIEW_PLANS, scope: Scope.ORG },
            { action: Action.VIEW_FORECASTS, scope: Scope.ORG },
            { action: Action.VIEW_RECOMMENDATIONS, scope: Scope.ORG },
            // Equipment: All except MANAGE_EQUIPMENT
            { action: Action.VIEW_EQUIPMENT, scope: Scope.ORG },
            { action: Action.VIEW_PARTS_INVENTORY, scope: Scope.ORG },
            { action: Action.MANAGE_PARTS_INVENTORY, scope: Scope.ORG },
            { action: Action.PERFORM_INSPECTIONS, scope: Scope.ORG },
            { action: Action.REPORT_BREAKDOWNS, scope: Scope.ORG },
            // Livestock: View only (mirrors today's VIEW_FARMS-gated read routes)
            { action: Action.VIEW_LIVESTOCK, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_RECORDS, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_MOVEMENTS, scope: Scope.ORG },
            // Economics / Feasibility: View only
            { action: Action.VIEW_FEASIBILITY, scope: Scope.ORG },
            // Financial Hub: View + Manage (D10 — gerentes log money events
            // from the field; SETTINGS + EXPORT stay owner/admin/accountant).
            { action: Action.VIEW_FINANCE, scope: Scope.ORG },
            { action: Action.MANAGE_FINANCE, scope: Scope.ORG },
            // AI Assistant
            { action: Action.USE_AI_ASSISTANT, scope: Scope.ORG },
            { action: Action.REVIEW_AI_PROPOSALS, scope: Scope.ORG },
        ],
    },
    {
        id: Role.ORG_MEDICAL_PROFESSIONAL,
        name: "Organization Medical Professional",
        grants: [
            { action: Action.VIEW_ANALYTICS, scope: Scope.ORG },
            { action: Action.USE_AI_ASSISTANT, scope: Scope.ORG },
            { action: Action.REVIEW_AI_PROPOSALS, scope: Scope.ORG },
        ],
    },
    {
        id: Role.ORG_ACCOUNTANT,
        name: "Organization Accountant",
        grants: [
            { action: Action.VIEW_SETTINGS, scope: Scope.ORG },
            { action: Action.VIEW_ANALYTICS, scope: Scope.ORG },
            // GPPMS: View only (for cost analysis)
            { action: Action.VIEW_FARMS, scope: Scope.ORG },
            { action: Action.VIEW_PADDOCKS, scope: Scope.ORG },
            { action: Action.VIEW_OPERATIONS, scope: Scope.ORG },
            { action: Action.VIEW_PLANS, scope: Scope.ORG },
            // Equipment: View only
            { action: Action.VIEW_EQUIPMENT, scope: Scope.ORG },
            { action: Action.VIEW_PARTS_INVENTORY, scope: Scope.ORG },
            // Livestock: View only (mirrors today's VIEW_FARMS-gated read routes)
            { action: Action.VIEW_LIVESTOCK, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_RECORDS, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_MOVEMENTS, scope: Scope.ORG },
            // Economics / Feasibility: View only
            { action: Action.VIEW_FEASIBILITY, scope: Scope.ORG },
            // Financial Hub: Full access (the accountant is a finance power user)
            { action: Action.VIEW_FINANCE, scope: Scope.ORG },
            { action: Action.MANAGE_FINANCE, scope: Scope.ORG },
            { action: Action.MANAGE_FINANCE_SETTINGS, scope: Scope.ORG },
            { action: Action.EXPORT_FINANCE, scope: Scope.ORG },
            // Payroll: salary-sensitive, owner/admin/accountant only (NOT manager).
            { action: Action.MANAGE_PAYROLL, scope: Scope.ORG },
            // AI Assistant
            { action: Action.USE_AI_ASSISTANT, scope: Scope.ORG },
            { action: Action.REVIEW_AI_PROPOSALS, scope: Scope.ORG },
        ],
    },
    {
        id: Role.ORG_VIEWER,
        name: "Organization Viewer",
        grants: [
            { action: Action.VIEW_ESTABLISHMENTS, scope: Scope.ORG },
            { action: Action.VIEW_ANALYTICS, scope: Scope.ORG },
            // GPPMS: View only
            { action: Action.VIEW_FARMS, scope: Scope.ORG },
            { action: Action.VIEW_PADDOCKS, scope: Scope.ORG },
            { action: Action.VIEW_CULTIVARS, scope: Scope.ORG },
            { action: Action.VIEW_OPERATIONS, scope: Scope.ORG },
            { action: Action.VIEW_MEASUREMENTS, scope: Scope.ORG },
            { action: Action.VIEW_PLANS, scope: Scope.ORG },
            { action: Action.VIEW_FORECASTS, scope: Scope.ORG },
            // Equipment: View only
            { action: Action.VIEW_EQUIPMENT, scope: Scope.ORG },
            { action: Action.VIEW_PARTS_INVENTORY, scope: Scope.ORG },
            // Livestock: View only (mirrors today's VIEW_FARMS-gated read routes)
            { action: Action.VIEW_LIVESTOCK, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_RECORDS, scope: Scope.ORG },
            { action: Action.VIEW_LIVESTOCK_MOVEMENTS, scope: Scope.ORG },
            // Economics / Feasibility: View only
            { action: Action.VIEW_FEASIBILITY, scope: Scope.ORG },
            // AI Assistant
            { action: Action.USE_AI_ASSISTANT, scope: Scope.ORG },
        ],
    },
];
//# sourceMappingURL=Authorization.js.map